/*
 * <go-campaign-announcement-bar>: campaign bar (sections/go-campaign-announcement-bar.liquid)
 * Countdown to `data-end`, hides the section at zero (or right away when a cached page is already
 * past the end), and alternates badge and message in one slot on mobile.
 * Loaded as a module by the section itself, only on pages where the bar renders.
 */

const CAMPAIGN_BAR_MOBILE_QUERY = '(max-width: 749px)';
const CAMPAIGN_BAR_DEFAULT_SLIDE_SECONDS = 4;

const padTime = (value) => String(value).padStart(2, '0');

class GoCampaignAnnouncementBar extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.setupCountdown();
    this.setupSlider(signal);

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) {
          this.stopCountdown();
          this.stopSlider();
        } else {
          this.startCountdown();
          this.startSlider();
        }
      },
      { signal }
    );
  }

  disconnectedCallback() {
    if (!this.abortController) return;

    this.stopCountdown();
    this.stopSlider();
    this.abortController.abort();
    this.abortController = null;
  }

  /* Countdown ------------------------------------------------------------------------------------ */

  setupCountdown() {
    this.timer = this.querySelector('[data-go-campaign-announcement-bar-countdown]');
    if (!this.timer) return;

    this.end = Date.parse(this.timer.dataset.end);
    if (Number.isNaN(this.end)) {
      this.timer = null;
      return;
    }

    this.units = {
      days: this.timer.querySelector('[data-unit="days"]'),
      hours: this.timer.querySelector('[data-unit="hours"]'),
      minutes: this.timer.querySelector('[data-unit="minutes"]'),
      seconds: this.timer.querySelector('[data-unit="seconds"]'),
    };
    this.startCountdown();
  }

  startCountdown() {
    if (!this.timer) return;
    this.stopCountdown();
    if (!this.tick()) return;

    // Align to the next full second so the numbers change in step with the wall clock.
    this.countdownTimeout = setTimeout(() => {
      if (!this.tick()) return;
      this.countdownInterval = setInterval(() => {
        if (!this.tick()) this.stopCountdown();
      }, 1000);
    }, 1000 - (Date.now() % 1000));
  }

  stopCountdown() {
    clearTimeout(this.countdownTimeout);
    clearInterval(this.countdownInterval);
  }

  /** Updates the cards; returns false (and hides the bar) once the campaign has ended. */
  tick() {
    const remaining = Math.floor((this.end - Date.now()) / 1000);
    if (remaining <= 0) {
      this.hide();
      return false;
    }

    const days = Math.floor(remaining / 86400);
    const hours = Math.floor((remaining % 86400) / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    const seconds = remaining % 60;

    this.units.days.textContent = padTime(days);
    this.units.hours.textContent = padTime(hours);
    this.units.minutes.textContent = padTime(minutes);
    this.units.seconds.textContent = padTime(seconds);

    // Minute precision keeps the label stable; aria-live="off" means it is read on focus, not every second.
    const labels = this.timer.dataset;
    const label = `${labels.labelPrefix} ${days} ${labels.labelDays} ${hours} ${labels.labelHours} ${minutes} ${labels.labelMinutes}`;
    if (this.timer.getAttribute('aria-label') !== label) this.timer.setAttribute('aria-label', label);
    return true;
  }

  hide() {
    this.stopCountdown();
    this.stopSlider();
    const section = this.closest('.shopify-section') || this;
    section.hidden = true;
    section.style.display = 'none';
  }

  /* Mobile slider -------------------------------------------------------------------------------- */

  setupSlider(signal) {
    this.slides = [...this.querySelectorAll('[data-go-campaign-announcement-bar-slide]')];
    if (this.slides.length < 2) {
      this.slides = null;
      return;
    }

    this.slideIndex = 0;
    this.slideDelay = (parseInt(this.dataset.slideInterval, 10) || CAMPAIGN_BAR_DEFAULT_SLIDE_SECONDS) * 1000;

    // Hold the current slide while a keyboard user is on the link.
    this.addEventListener('focusin', () => this.stopSlider(), { signal });
    this.addEventListener('focusout', () => this.startSlider(), { signal });

    this.mediaQuery = window.matchMedia(CAMPAIGN_BAR_MOBILE_QUERY);
    this.mediaQuery.addEventListener('change', () => this.applySliderMode(), { signal });
    this.applySliderMode();
  }

  applySliderMode() {
    const enabled = this.mediaQuery.matches;
    const focusedSlide = this.slides.find((slide) => slide.matches(':focus-within'));
    if (enabled && focusedSlide) this.slideIndex = this.slides.indexOf(focusedSlide);
    this.classList.toggle('go-campaign-announcement-bar--slider', enabled);
    this.showSlide(enabled ? this.slideIndex : -1);
    if (enabled) this.startSlider();
    else this.stopSlider();
  }

  /** Marks one slide active; -1 clears slider state (desktop shows both). */
  showSlide(index) {
    this.slides.forEach((slide, i) => {
      const active = i === index;
      slide.classList.toggle('is-active', active);
      if (index === -1 || active) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    });
  }

  startSlider() {
    if (!this.slides || !this.mediaQuery.matches || document.hidden || this.matches(':focus-within')) return;
    this.stopSlider();
    this.slideInterval = setInterval(() => {
      this.slideIndex = (this.slideIndex + 1) % this.slides.length;
      this.showSlide(this.slideIndex);
    }, this.slideDelay);
  }

  stopSlider() {
    clearInterval(this.slideInterval);
  }
}

if (!customElements.get('go-campaign-announcement-bar')) {
  customElements.define('go-campaign-announcement-bar', GoCampaignAnnouncementBar);
}
